import Cookies from 'js-cookie';
import { isBrowser } from './ssr';
import { BROWSER_GUID_COOKIE } from './identification';

/**
 * Creates an error object and includes the response.
 * This is important to be able to distinguish server errors (with response) from network errors (no response).
 * @returns {Error} error Error object
 * @returns {Object} error.response Server response
 */
const makeErrorWithResponse = (response, json) => {
  const fullError = new Error(
    json.message || response.statusText || response.status
  );
  fullError.response = json || response;
  fullError.response.statusCode = response.status;
  return fullError;
};

/**
 * Low-level request generator
 * @param {String} method HTTP method to execute
 * @param {String} uri Location of resource
 * @param {*} data Data to send with request. Objects will be stringified.
 * @param {Object} opts Additional options to include in underlying fetch call. Valid properties: https://developer.mozilla.org/en-US/docs/Web/API/Request
 * @returns {Promise} Resolved if successful response from server, rejected if network (error.response) or server error.
 */
export function send(method, uri, data, opts = {}) {
  const queryParams = new URLSearchParams(
    isBrowser() ? window.location.search : ''
  );
  const previewMode = queryParams.get('preview');
  const debugMode = queryParams.get('debug') === 'true';

  return new Promise(async (resolve, reject) => {
    const options = { ...opts };
    let body = data;

    // NOTE: We need to ensure Headers constructor receives a truthy value otherwise this will break in Edge 16.
    options.headers = new Headers(options.headers || {});

    if (!options.is3rdParty) {
      // Inject Platform API required headers
      options.headers.set('X-Region', 'US');

      const bomCountryCookie = isBrowser()
        ? Cookies.get('BOM-COUNTRY')
        : undefined;

      if (bomCountryCookie) {
        const [countryCode] = bomCountryCookie.split(':');
        options.headers.set('x-bom-country', countryCode);
      }

      const browserGuid = isBrowser()
        ? Cookies.get(BROWSER_GUID_COOKIE)
        : undefined;
      if (browserGuid) {
        options.headers.set('X-Bombas-GUID', browserGuid);
      }

      // Set preview mode header, if applicable.
      if (previewMode) {
        options.headers.set('X-Preview', previewMode);
      }

      // Set debug mode header, if applicable.
      if (debugMode) {
        options.headers.set('X-Bombas-Debug', 'true');
      }
    }

    // Stringify JSON payloads
    if (body && typeof body === 'object') {
      options.headers.set('Content-Type', 'application/json');
      body = JSON.stringify(body);
    }

    await fetch(uri, {
      method,
      body,
      ...options
    })
      // Receives response from server
      .then(async (res) => {
        let json;

        // Automatically parse JSON
        if (res.headers.get('content-type')?.includes('application/json')) {
          json = await res.json();
        }

        if (!res.ok) throw makeErrorWithResponse(res, json);
        return resolve(json || res.body);
      })
      // Handle network & server errors
      .catch(reject);
  });
}

/**
 * Send a GET request
 * @param {String} uri Location of resource
 * @param {Object} [params] Additional options to include in underlying fetch call. Valid properties: https://developer.mozilla.org/en-US/docs/Web/API/Request
 * @returns {Promise} Resolved if successful response from server, rejected if network (error.response) or server error.
 */
export const get = (uri, params) => send('GET', uri, null, params);
/**
 * Send a POST request
 * @param {String} uri Location of resource
 * @param {*} [data] Data to send with request. Objects will be stringified.
 * @param {Object} [params] Additional options to include in underlying fetch call. Valid properties: https://developer.mozilla.org/en-US/docs/Web/API/Request
 * @returns {Promise} Resolved if successful response from server, rejected if network (error.response) or server error.
 */
export const post = send.bind(null, 'POST');
